import { useEffect, useRef } from "react";

const VERT_SRC = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

// Aurora Borealis – realistic vertical rays, horizontal arc, dark sky, stars
const FRAG_SRC = `
precision highp float;
uniform float u_time;
uniform vec2  u_resolution;

// ── hash / noise ─────────────────────────────────────────────────────────────
float hash21(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash21(i),                 hash21(i + vec2(1.0, 0.0)), f.x),
    mix(hash21(i + vec2(0.0,1.0)), hash21(i + vec2(1.0,1.0)), f.x),
    f.y
  );
}

// 4-octave fBm – kept lean for mobile GPUs
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p  = p * 2.1 + vec2(3.1, 1.7);
    a *= 0.5;
  }
  return v;
}

// ── star field ───────────────────────────────────────────────────────────────
float stars(vec2 uv) {
  // Correct for aspect ratio so star cells are always pixel-square.
  // Without this, stars stretch horizontally on wide/ultrawide screens.
  float aspect = u_resolution.x / u_resolution.y;
  // Clamp to a sane range so extreme ratios don't explode star counts.
  aspect = clamp(aspect, 0.5, 3.0);
  vec2 suv = vec2(uv.x * aspect, uv.y);

  float s = 0.0;

  // sparse bright stars
  vec2 c1 = floor(suv * 150.0);
  vec2 l1 = fract(suv * 150.0) - 0.5;
  float r1 = hash21(c1);
  if (r1 > 0.92) {
    float twinkle = 0.6 + 0.4 * sin(u_time * (1.2 + r1 * 2.5) + r1 * 39.0);
    s += smoothstep(0.20, 0.02, length(l1)) * twinkle * pow((r1 - 0.92) / 0.08, 1.5);
  }

  // dense dim stars
  vec2 c2 = floor(suv * 320.0);
  vec2 l2 = fract(suv * 320.0) - 0.5;
  float r2 = hash21(c2 + vec2(7.3, 5.1));
  if (r2 > 0.87) {
    s += smoothstep(0.14, 0.01, length(l2)) * 0.35 * ((r2 - 0.87) / 0.13);
  }

  return s;
}

// ── vertical ray structure ────────────────────────────────────────────────────
// Simulates the thin vertical striations of real aurora
float rays(float x, float t, float freq, float speed, float seed) {
  // Multiple overlapping sine/noise frequencies give ray "fingers"
  float r = 0.0;
  r += 0.50 * noise(vec2(x * freq        + seed,          t * speed));
  r += 0.30 * noise(vec2(x * freq * 2.1  + seed + 4.3,    t * speed * 1.4));
  r += 0.15 * noise(vec2(x * freq * 4.7  + seed + 8.9,    t * speed * 0.7));
  r += 0.05 * noise(vec2(x * freq * 11.3 + seed + 2.1,    t * speed * 2.1));
  return r;
}

// ── single aurora band ────────────────────────────────────────────────────────
// baseY   : vertical centre of the band (0-1)
// height  : half-height of the band
// seed    : per-band random offset
// Returns RGB contribution
vec3 auroraBand(
  vec2 uv, float t,
  float baseY, float height,
  vec3 colEdge, vec3 colCore, vec3 colTop,
  float speed, float seed
) {
  // Slow horizontal drift of the arc centre
  float drift = 0.018 * sin(t * speed * 0.4 + seed * 2.0)
              + 0.010 * sin(t * speed * 0.9 + seed * 5.1);

  // Vertical position of the arc at this x (gentle curve)
  float arc = baseY + drift
            + 0.022 * sin(uv.x * 2.8 + t * speed * 0.3 + seed)
            + 0.012 * sin(uv.x * 5.5 + t * speed * 0.5 + seed * 1.7);

  // Ray-modulated height: rays stretch upward from the arc
  float rayVal = rays(uv.x, t, 6.0, speed, seed);
  float rayStretch = height * (0.6 + 1.4 * rayVal);      // 0.6–2.0 × base height

  // Below arc: narrow crisp lower edge
  float distBelow = arc - uv.y;                           // positive when pixel is below arc
  float distAbove = uv.y - arc;                           // positive when above

  float envBelow = smoothstep(height * 0.35, 0.0, distBelow);
  float envAbove = smoothstep(rayStretch,    0.0, distAbove);

  // Combined envelope — sharp lower, diffuse upper rays
  float env = max(envBelow, envAbove) * step(0.0, uv.y);  // clip to y>0

  // Colour: bright electric green at core/lower edge, teal→dark at ray tips
  float t_upper = clamp(distAbove / (rayStretch + 0.001), 0.0, 1.0);
  float t_lower = clamp(distBelow / (height * 0.35 + 0.001), 0.0, 1.0);

  vec3 col = colCore;
  col = mix(col, colEdge,  t_lower);   // lower edge fades to edge colour
  col = mix(col, colTop,   t_upper);   // ray tips fade to top colour

  // Per-ray brightness variation — prevents uniform appearance
  float bright = 0.55 + 0.45 * rayVal;

  return col * env * bright;
}

// ── main ─────────────────────────────────────────────────────────────────────
void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  float t  = u_time * 0.12;   // overall slow pace

  // ── near-black night sky ──────────────────────────────────────────────────
  // Very dark — real northern-sky photos are almost pitch black
  vec3 sky = mix(vec3(0.005, 0.008, 0.020),   // horizon: dark blue-black
                 vec3(0.000, 0.003, 0.012),   // zenith: near-black
                 uv.y);

  // Stars — visible but not overpowering; dim near horizon
  float starMask = smoothstep(0.05, 0.28, uv.y);
  float s = stars(uv) * starMask;
  sky += vec3(0.80, 0.88, 1.00) * s * 0.55;

  // ── aurora bands ─────────────────────────────────────────────────────────
  //  Real aurora: dominated by yellow-green (557.7 nm), pink/red fringe at top

  // Main green band — strongest, sits in middle sky
  vec3 b1 = auroraBand(uv, t,
    0.52, 0.07,
    vec3(0.00, 0.55, 0.20),   // lower edge: deep green
    vec3(0.02, 0.88, 0.32),   // core: electric green
    vec3(0.00, 0.18, 0.12),   // ray tops: dark teal
    0.45, 0.00);

  // Secondary green band — offset, slightly lower & thinner
  vec3 b2 = auroraBand(uv, t,
    0.44, 0.045,
    vec3(0.00, 0.45, 0.25),
    vec3(0.01, 0.72, 0.28),
    vec3(0.00, 0.12, 0.10),
    0.38, 5.73);

  // Pink/red fringe — only at the very top of tall rays (real N2+ emission)
  vec3 b3 = auroraBand(uv, t,
    0.60, 0.035,
    vec3(0.50, 0.05, 0.18),   // edge: deep crimson
    vec3(0.78, 0.12, 0.30),   // core: rose-pink
    vec3(0.25, 0.02, 0.10),   // tops: dark wine
    0.50, 11.2);

  // Diffuse cyan/teal glow between bands (scattered light)
  vec3 b4 = auroraBand(uv, t,
    0.48, 0.10,
    vec3(0.00, 0.30, 0.28),
    vec3(0.00, 0.38, 0.32),
    vec3(0.00, 0.08, 0.10),
    0.28, 8.44);

  // ── compose ───────────────────────────────────────────────────────────────
  // Keep intensities restrained — real aurora is subtle
  vec3 aurora = b1 * 0.85
              + b2 * 0.60
              + b3 * 0.40   // pink is a hint, not dominant
              + b4 * 0.25;  // cyan haze is very dim

  // Soft localised glow (scattering) — add around bright aurora areas
  float lum = dot(aurora, vec3(0.299, 0.587, 0.114));
  aurora += vec3(0.00, 0.12, 0.08) * pow(lum, 1.8) * 1.2;

  // Aurora only lives above horizon
  float horizonFade = smoothstep(0.08, 0.22, uv.y);
  aurora *= horizonFade;

  // ── final composite ───────────────────────────────────────────────────────
  vec3 col = sky + aurora;

  // Mild vignette — darkens corners like a wide-angle photo
  float vig = 1.0 - 0.35 * pow(length((uv - 0.5) * 1.6), 2.0);
  col *= clamp(vig, 0.0, 1.0);

  col = clamp(col, 0.0, 1.0);
  // Subtle gamma-like tone: lift blacks just a hair for screen rendering
  col = pow(col, vec3(0.95));

  gl_FragColor = vec4(col, 1.0);
}
`;

// ── WebGL helpers ─────────────────────────────────────────────────────────────
function createShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error("Aurora shader compile error:", gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}

function createProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram | null {
  const vs = createShader(gl, gl.VERTEX_SHADER, vert);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, frag);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error("Aurora program link error:", gl.getProgramInfoLog(prog));
    return null;
  }
  return prog;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function WebGLAuroraBorealisBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", { alpha: false, antialias: false });
    if (!gl) return;

    const program = createProgram(gl, VERT_SRC, FRAG_SRC);
    if (!program) return;

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    const posLoc  = gl.getAttribLocation(program, "a_position");
    const timeLoc = gl.getUniformLocation(program, "u_time");
    const resLoc  = gl.getUniformLocation(program, "u_resolution");

    if (gl.isContextLost()) return;
      gl.useProgram(program);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    let start: number | null = null;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    const render = (ts: number) => {
      if (start === null) start = ts;
      gl.uniform1f(timeLoc, (ts - start) / 1000);
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      gl.deleteBuffer(buf);
      gl.deleteProgram(program);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position:      "absolute",
        inset:         0,
        width:         "100%",
        height:        "100%",
        display:       "block",
        zIndex:        0,
        pointerEvents: "none",
      }}
    />
  );
}
